import React, { useState } from "react";
import { Modal } from "../../../components/modal/Modal";
import { useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { EntriesTableSettings } from "../../../model/entries-table-settings/EntriesTableSettings";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import styled from "styled-components";
import { AtiraCheckBoxGroup } from "../../../components/AtiraCheckBoxGroup";
import { Spacing } from "../../../theme/Spacing";
import { getTranslatedTableColumnLabel } from "../utils/getTranslatedTableColumnLabel";
import { getEntriesExportFileName } from "../utils/getEntriesExportFileName";
import { Button } from "../../../components/Button";
import { useDrawersContext } from "../../tasks/drawers-context";
import { AtiraToast } from "../../../utils/AtiraToast";
import { entryActions } from "../../../redux/entry/entry.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { getTypedCRUDErrors } from "../../../utils/getTypedCRUDErros";

const StyledAtiraCheckBoxGroup = styled(AtiraCheckBoxGroup)`
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Spacing.s};

  .ant-checkbox-wrapper {
    flex: 1 1 45%;
    white-space: normal;
  }
`;

export const EntryBulkExportModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    bulkEntryExportVisible,
    setBulkEntryExportVisible,
    selectedRows: selectedEntriesIds,
    setSelectedRows: setSelectedEntriesIds,
  } = useDrawersContext();

  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  const allColumns =
    tableSettings?.columns.filter(
      (col) => !["actions", "assignee", "task", "tags"].includes(col.field),
    ) || [];
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const [selectedHeaders, setSelectedHeaders] =
    useState<EntriesTableSettings["columns"]>(allColumns);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { t } = useTranslation();

  const localOnClose = () => {
    setBulkEntryExportVisible(false);
    setSelectedEntriesIds([]);
    setSelectedHeaders(allColumns);
  };

  const onSetErrors = (e: any) => {
    const errors = getTypedCRUDErrors(e);

    if (errors?.length) {
      // A typed error's message is a translation key, its params fill it in
      setErrorMessage(t(errors[0].message, errors[0].params));
    } else if (e.response?.data) {
      setErrorMessage(t(e.response.data));
    }
  };

  const onExport = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        entryActions.exportEntries({
          userId,
          selectedEntriesIds: selectedEntriesIds as string[],
          selectedHeaders: selectedHeaders.map((col) => col.field),
        }),
      ).unwrap();

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        getEntriesExportFileName({
          count: selectedEntriesIds.length,
          scope: "selected",
        }),
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      AtiraToast.success(
        t("entries.table.bulkHeader.export.success", {
          entries_count: selectedEntriesIds.length,
        }),
      );
      localOnClose();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      onSetErrors(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("entries.table.entry.export.bulk.title", {
        count: selectedEntriesIds.length,
      })}
      open={bulkEntryExportVisible}
      onClose={localOnClose}
    >
      <Flex flexDirection="column" gap="m">
        <Flex flexDirection="column">
          <Text fontWeight="bold">
            {t("entries.export.modal.selectHeaders.title")}
          </Text>
          <Text fontSize="s" color="darkerSub">
            {t("entries.export.modal.selectHeaders.description")}
          </Text>
        </Flex>

        <StyledAtiraCheckBoxGroup
          options={allColumns.map((col) => ({
            label: getTranslatedTableColumnLabel(col),
            value: col.field,
          }))}
          value={selectedHeaders.map((col) => col.field)}
          style={{ flexWrap: "wrap" }}
          columnGap="7rem"
          onChange={(values) =>
            setSelectedHeaders(
              allColumns.filter((col) => values.includes(col.field)),
            )
          }
        />

        {errorMessage ? <Text color="danger">* {errorMessage}</Text> : null}
        <Button
          disabled={!selectedHeaders.length}
          loading={loading}
          title={t("common.export")}
          onClick={onExport}
          margin="none"
        />
      </Flex>
    </Modal>
  );
};
